import { FormattedMessage } from 'react-intl'
import { Link } from '@tanstack/react-router'

import { IconCrownUpgrade } from '@gto/ui/assets'
import { Button } from '@gto/ui/button'
import { cn } from '@gto/ui/cn'
import { InformationCircleIcon } from '@gto/ui/icons/outline'
import { Typography } from '@gto/ui/typography'

import {
  ArrowRightSmallIcon,
  GlyphsDollarBillWaveBoldIcon,
  HealthiconsMoneyBagIcon,
  IcTwotoneMoneyOffCsredIcon,
} from '@/assets/icons'
import type { Translations } from '@/localise'

type FreeBankrollPrizePoolSectionProps = {
  isAuthorized: boolean
}

export const FreeBankrollPrizePoolSection = ({
  isAuthorized,
}: FreeBankrollPrizePoolSectionProps) => {
  return (
    <section className="flex flex-col items-center pb-[104px] pt-[52px] md:pb-[40px] md:pt-[40px]">
      <Typography
        className="text-center"
        component="h2"
        variant="h4"
      >
        <FormattedMessage id="freeBankroll.prizes.title" />
      </Typography>

      <div className="mt-[64px] flex w-full flex-row gap-4 md:mt-[32px] md:flex-col">
        <PrizeCard
          isFeatured
          bankrollIcon={GlyphsDollarBillWaveBoldIcon}
          bankrollId="freeBankroll.prizes.grand.bankroll"
          noteId="freeBankroll.prizes.note"
          subscriptionId="freeBankroll.prizes.grand.subscription"
          titleId="freeBankroll.prizes.grand.title"
          winnersId="freeBankroll.prizes.grand.winners"
        />
        <PrizeCard
          bankrollIcon={HealthiconsMoneyBagIcon}
          bankrollId="freeBankroll.prizes.standard.bankroll"
          noteId="freeBankroll.prizes.note"
          subscriptionId="freeBankroll.prizes.standard.subscription"
          titleId="freeBankroll.prizes.standard.title"
          winnersId="freeBankroll.prizes.standard.winners"
        />
      </div>

      {!isAuthorized && (
        <div className="mt-[64px] md:mt-[32px]">
          <Button
            as={Link}
            preload={false}
            size="large"
            to="/login"
            variant="contained"
            endIcon={
              <div className="inline-flex size-6">
                <ArrowRightSmallIcon
                  height={24}
                  width={24}
                />
              </div>
            }
          >
            <FormattedMessage id="common.controls.createAccount" />
          </Button>
        </div>
      )}
    </section>
  )
}

type PrizeCardProps = {
  titleId: keyof Translations
  winnersId: keyof Translations
  bankrollId: keyof Translations
  bankrollIcon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  subscriptionId: keyof Translations
  noteId: keyof Translations
  isFeatured?: boolean
}

const PrizeCard = ({
  titleId,
  winnersId,
  bankrollId,
  bankrollIcon: BankrollIcon,
  subscriptionId,
  noteId,
  isFeatured = false,
}: PrizeCardProps) => {
  return (
    <div
      className={cn(
        'relative flex flex-1 flex-col gap-4 rounded-md border bg-paper-elevation1 p-6',
        isFeatured ? 'border-primary-main' : 'border-divider',
      )}
    >
      <div
        className={cn(
          'absolute -top-[12px] right-[12px] flex items-center gap-1.5 rounded-xl border bg-paper-elevation3 px-3 py-0.5',
          isFeatured
            ? 'border-warning-main text-warning-main'
            : 'border-divider text-text-secondary',
        )}
      >
        {isFeatured && (
          <IconCrownUpgrade
            color="currentColor"
            height={20}
            width={20}
          />
        )}
        <Typography
          color="inherit"
          variant="body3"
        >
          <FormattedMessage id={winnersId} />
        </Typography>
      </div>

      <div className="flex items-center gap-2">
        <Typography
          className="text-primary-main"
          variant="h6"
        >
          <FormattedMessage id={titleId} />
        </Typography>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <div className="flex size-[52px] items-center justify-center rounded-xs bg-primary-states-focus text-primary-main">
            <BankrollIcon
              height={36}
              width={36}
            />
          </div>
          <div className="flex flex-col justify-center">
            <Typography
              className="font-bold"
              color="text.primary"
              variant="body1"
            >
              <FormattedMessage id={bankrollId} />
            </Typography>
            <Typography
              className="text-[12px] font-normal leading-[150%] tracking-[0.15px]"
              color="text.primary"
              variant="body3"
            >
              <FormattedMessage id="freeBankroll.prizes.bankrollLabel" />
            </Typography>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex size-[52px] items-center justify-center rounded-xs bg-primary-states-focus text-primary-main">
            <IcTwotoneMoneyOffCsredIcon
              height={36}
              width={36}
            />
          </div>
          <div className="flex flex-col justify-center">
            <Typography
              className="font-bold"
              color="text.primary"
              variant="body1"
            >
              <FormattedMessage id="freeBankroll.prizes.oneFreeMonth" />
            </Typography>
            <Typography
              className="text-[12px] font-normal leading-[150%] tracking-[0.15px]"
              color="text.primary"
              variant="body3"
            >
              <FormattedMessage id={subscriptionId} />
            </Typography>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 rounded-sm bg-paper-elevation4 px-2.5 py-1.5">
        <InformationCircleIcon
          className="text-grey-600"
          color="inherit"
          height={18}
          style={{ flex: '0 0 auto' }}
          width={18}
        />
        <Typography
          className="text-grey-600"
          variant="body3"
        >
          <FormattedMessage id={noteId} />
        </Typography>
      </div>
    </div>
  )
}
